import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Model from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, or } from 'ember-truth-helpers';
import PdfUploader from 'garaje/components/pdf-uploader';
import UiCard from 'garaje/components/ui/card';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiInputField from 'garaje/components/ui-input-field';
import TranslatableContent from 'garaje/components/visitors/questionnaires/form/translatable-content';
import featureEnabled from 'garaje/helpers/feature-enabled';
import type QuestionnaireSection from 'garaje/models/questionnaire-section';
import type { LocalizedQuestionnaireSection } from 'garaje/models/questionnaire-section';
import type { LocaleKey } from 'garaje/utils/locale-options';
import { cached } from 'tracked-toolbox';
import type { FileUpload } from 'utils/direct-uploader';

// Make this string a constant rather than just hard-coding it in the template because "\n" gets escaped that way, and we want
// the newline to be interpreted as (and display as) as a newline.
const VIDEO_INSTRUCTIONS_PLACEHOLDER_TEXT =
  'Prepare assessment takers for what to expect.\nExample: “Make sure to watch the video until the end.” or “Be sure to pay attention, there will be a quiz right after the video.”';

interface SectionContentSignature {
  Args: {
    canDeleteSection: boolean;
    defaultLocale: LocaleKey;
    deleteSection: (section: QuestionnaireSection) => void;
    onChange: (models: Model[]) => void;
    readOnly?: boolean;
    section: QuestionnaireSection;
    selectedLocale: LocaleKey;
  };
  Element: HTMLDivElement;
}

function hasPDF(section: LocalizedQuestionnaireSection): boolean {
  return !!section.pdfContent?.file?.name;
}

export default class SectionContent extends Component<SectionContentSignature> {
  @tracked isOpen = true;

  @cached
  get defaultLocaleSection(): LocalizedQuestionnaireSection {
    const { defaultLocale, section } = this.args;
    return section.forLocale(defaultLocale, defaultLocale);
  }

  get hasPDF(): boolean {
    return hasPDF(this.localizedSection);
  }

  get inputId(): string {
    return `description-${guidFor(this.args.section)}`;
  }

  get isTranslating(): boolean {
    return this.args.selectedLocale !== this.args.defaultLocale;
  }

  @cached
  get localizedSection(): LocalizedQuestionnaireSection {
    const { defaultLocale, section, selectedLocale } = this.args;
    return section.forLocale(selectedLocale, defaultLocale);
  }

  get showDeleteSectionButton(): boolean {
    return !this.args.readOnly && this.args.canDeleteSection && !this.isTranslating;
  }

  get showPDFTranslationMessage(): boolean {
    // Show a MessageBox above the PDF uploader when we're viewing a language other than the default,
    // the default language has an associated PDF, and the current language does not.
    if (!this.isTranslating) return false;
    return hasPDF(this.defaultLocaleSection) && !this.hasPDF;
  }

  @action
  removePDF(): void {
    const localizedSection = this.localizedSection;
    localizedSection.pendingPDFUpload = null;
    localizedSection.pdf = { filename: null, 'pdf-url': null, 'updated-at': null, id: null };
    this.args.onChange([localizedSection.section]);
  }

  @action
  toggleCollapsed(): void {
    this.isOpen = !this.isOpen;
  }

  @action
  updateAllowSkipVideo(): void {
    const section = this.args.section;
    section.allowSkipVideo = !section.allowSkipVideo;
    this.args.onChange([section]);
  }

  @action
  updateDescription(event: Event): void {
    const localizedSection = this.localizedSection;
    const target = event.target as HTMLTextAreaElement;
    localizedSection.description = target.value;
    this.args.onChange([localizedSection.section]);
  }

  @action
  updatePendingPDFUpload(upload: FileUpload | undefined): void {
    const localizedSection = this.localizedSection;
    localizedSection.pendingPDFUpload = upload ?? null;
    this.args.onChange([localizedSection.section]);
  }

  @action
  updateVideoInstructions(event: Event): void {
    const localizedSection = this.localizedSection;
    const target = event.target as HTMLTextAreaElement;
    localizedSection.videoInstructions = target.value;
    this.args.onChange?.([localizedSection.section]);
  }

  @action
  updateVideoUrl(url: string): void {
    const localizedSection = this.localizedSection;
    localizedSection.videoUrl = url;
    this.args.onChange?.([localizedSection.section]);
  }

  <template>
    <SettingsPanel @isExpandable={{true}} @isOpen={{this.isOpen}} ...attributes as |panel|>
      <panel.header @showCancel={{false}} as |head|>
        <head.title>
          <header class="!font-normal text-lg">
            <strong>Section {{@section.position}}</strong>
            Content
          </header>
        </head.title>
        <head.controls>
          {{#if this.showDeleteSectionButton}}
            <button
              type="button"
              class="btn btn--smaller btn--link hover:opacity-75 mr-4"
              {{on "click" (fn @deleteSection @section)}}
              data-test-delete-section={{@section.position}}
            >
              {{svgJar "trash-delete-black" height="16" width="16"}}
            </button>
          {{/if}}
          <button type="button" data-test-expand-collapse-toggle {{on "click" this.toggleCollapsed}}>
            {{#if panel.isOpen}}
              {{svgJar "chevron-up" height="10" width="10"}}
            {{else}}
              {{svgJar "chevron-down" height="10" width="10"}}
            {{/if}}
          </button>
        </head.controls>
      </panel.header>

      <panel.body>
        <div class="mt-4" data-test-section-content="description">
          <label class="!text-carbon-70 {{unless @readOnly 'cursor-pointer'}}">
            Description
            <TranslatableContent
              @defaultLocale={{@defaultLocale}}
              @selectedLocale={{@selectedLocale}}
              @showLanguagePill={{true}}
            >
              <:original>
                <span class="text-carbon-50" data-test-original-language-description>{{@section.description}}</span>
              </:original>
              <:selected>
                <textarea
                  name="description"
                  id={{this.inputId}}
                  disabled={{@readOnly}}
                  placeholder="Provide a description for your visitors"
                  {{on "input" this.updateDescription}}
                  data-test-section-description
                  value={{this.localizedSection.description}}
                >{{this.localizedSection.description}}</textarea>
              </:selected>
            </TranslatableContent>
          </label>
        </div>

        <span class="font-bold">Show multimedia content</span>
        <UiCard class="bg-carbon-5">
          {{#if (featureEnabled "visitors-questionnaires-watch-entire-video")}}
            <UiInputField data-test-section-content="videoInstructions" as |Field|>
              <Field.label class="!text-carbon-70 {{unless @readOnly 'cursor-pointer'}}">
                Instructions

                <TranslatableContent
                  @defaultLocale={{@defaultLocale}}
                  @selectedLocale={{@selectedLocale}}
                  @showLanguagePill={{true}}
                >
                  <:original>
                    <span
                      class="text-carbon-50"
                      data-test-original-language-video-instructions
                    >{{@section.videoInstructions}}</span>
                  </:original>
                  <:selected>
                    <textarea
                      name="video-instructions"
                      disabled={{@readOnly}}
                      placeholder={{VIDEO_INSTRUCTIONS_PLACEHOLDER_TEXT}}
                      {{on "input" this.updateVideoInstructions}}
                      data-test-section-video-instructions
                      value={{this.localizedSection.videoInstructions}}
                    >{{this.localizedSection.videoInstructions}}</textarea>
                  </:selected>
                </TranslatableContent>
              </Field.label>
            </UiInputField>
          {{/if}}

          <UiInputField data-test-section-content="videoUrl" as |Field|>
            <Field.label class="!text-carbon-70 {{unless @readOnly 'cursor-pointer'}}">
              Video
            </Field.label>

            <TranslatableContent
              @defaultLocale={{@defaultLocale}}
              @selectedLocale={{@selectedLocale}}
              @showLanguagePill={{false}}
            >
              <:original>
                <div class="flex px-4 py-2 items-center" data-test-original-language-video-url>
                  <div class="flex-0 border w-16 h-16 flex">
                    {{svgJar "video-camera" class="m-auto"}}
                  </div>
                  <div class="flex-1 pl-4">
                    {{#if @section.videoUrl}}
                      <a href={{@section.videoUrl}} target="_blank" rel="noopener">
                        {{@section.videoUrl}}
                      </a>
                    {{else}}
                      <p class="text-sm text-carbon-50 italic mb-0">No video URL configured</p>
                    {{/if}}
                  </div>
                </div>
              </:original>
              <:selected as |isTranslating defaultLanguageName|>
                {{#if (and @section.videoUrl isTranslating)}}
                  <MessageBox @type="info" class="!max-w-full mb-4">
                    Add or configure a video URL if you do not want to use content from your default language ({{defaultLanguageName}}).
                  </MessageBox>
                {{/if}}
                <Field.input
                  data-test-section-video-url
                  @placeholder="Video URL"
                  @value={{this.localizedSection.videoUrl}}
                  @disabled={{@readOnly}}
                  @update={{this.updateVideoUrl}}
                />
              </:selected>
            </TranslatableContent>
          </UiInputField>

          {{#if (featureEnabled "visitors-questionnaires-watch-entire-video")}}
            <UiInputField data-test-section-content="allowSkipVideo" as |Field|>
              <Field.input
                type="checkbox"
                disabled={{or @readOnly this.isTranslating}}
                checked={{@section.allowSkipVideo}}
                @update={{this.updateAllowSkipVideo}}
                data-test-allow-skip-video
              />

              <Field.label class="!text-carbon-70 {{unless @readOnly 'cursor-pointer'}}">
                Allow user to skip video
              </Field.label>
            </UiInputField>
          {{/if}}

          <div class="mt-4" data-test-section-content="pdf">
            <label class="!text-carbon-70" for="{{this.inputId}}-pdf-upload">
              Show content from a PDF
            </label>

            <TranslatableContent
              @defaultLocale={{@defaultLocale}}
              @selectedLocale={{@selectedLocale}}
              @showLanguagePill={{false}}
            >
              <:original>
                <div class="flex px-4 py-2 items-center" data-test-original-language-pdf>
                  <div class="flex-0 border w-16 h-16 flex">
                    {{svgJar "document" class="m-auto"}}
                  </div>
                  <div class="flex-1 pl-4 text-carbon-50">
                    {{#if @section.pdf.filename}}
                      {{@section.pdf.filename}}
                    {{else}}
                      <p class="text-sm italic mb-0">No PDF uploaded</p>
                    {{/if}}
                  </div>
                </div>
              </:original>
              <:selected as |_isTranslating defaultLanguageName|>
                <div class="mb-4">
                  {{#if this.showPDFTranslationMessage}}
                    <MessageBox @type="info" class="!max-w-full mb-4">
                      Add or upload content below if you do not want to use content from your default language ({{defaultLanguageName}}).
                    </MessageBox>
                  {{/if}}

                  <PdfUploader
                    @disabled={{@readOnly}}
                    @inputId="{{this.inputId}}-pdf-upload"
                    @onPendingUpload={{this.updatePendingPDFUpload}}
                    @reset={{this.removePDF}}
                    @upload={{this.localizedSection.pdfContent}}
                    @uploaderDisabled={{this.hasPDF}}
                    @useStandaloneFileUpload={{true}}
                    class="bg-white"
                    data-test-pdf
                  >
                    <:error as |message|>
                      <p class="text-papaya-50 mt-2" data-test-legal-doc-validation-message>{{message}}</p>
                    </:error>
                  </PdfUploader>
                </div>
              </:selected>
            </TranslatableContent>
          </div>
        </UiCard>
      </panel.body>
    </SettingsPanel>
  </template>
}
